import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "./utils/api";
import { resolveEndpoint } from "./utils/utils";
import { Instrument } from "./RealtimeChart";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { ArticleViewer } from "./ArticleViewer";

type SseStatus = "disconnected" | "stale" | "current";

interface NewItemRequest {
  instrument_id: number;
  ticker: string;
  notes: string;
  sector: string;
  company_name: string;
  type: string;
  exchange_name: string;
}

interface WatchlistItem {
  investing_id: number;
  ticker: string;
  notes: string;
  last_price: number;
  change_abs: number;
  change_rel: number;
  sector: string;
  company_name: string;
  type: string;
  exchange_name: string;
  regular_hours_last: number;
  regular_hours_change_abs: number;
  regular_hours_change_rel: number;
  premarket_active: boolean;
  premarket_last: number;
  premarket_change_abs: number;
  premarket_change_rel: number;
  intercepted: InterceptedItem[];
  marked: string | null;
  next_earnings: string | null;
  notification_percentage_step: number | null;
  last_notified: number | null;
  last_notification_level: number | null;
  move_explanation: MoveExplanation | null;
}

// why the stock moved this session (backend news_data/move_explainer.py, filled on a +-10% crossing)
interface MoveExplanation {
  status: "running" | "pending" | "explained" | "failed";
  attempts: number;
  max_attempts: number;
  next_retry_at: string | null;
  level: number | null;
  change_rel: number | null;
  reason: string | null;
  confidence: string | null;
  sources: string[];
  updated_at: string | null;
}

const MoveExplanationBlock = ({ m }: { m: MoveExplanation }) => {
  const nyTime = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false });
  const label = m.level ? `Move ${m.level > 0 ? "+" : ""}${m.level}%:` : "Move:";
  let text: React.ReactNode;
  if (m.status === "explained") {
    text = (
      <>
        {m.reason} <span className="text-gray-500">[{m.confidence}]</span>
        {m.sources.map((u, i) => (
          <a key={u} href={u} target="_blank" rel="noreferrer" className="ml-2 text-gray-500 hover:underline" title={u}>
            ↗{i + 1}
          </a>
        ))}
      </>
    );
  } else if (m.status === "failed") {
    text = <span className="text-gray-500">no reason found ({m.attempts} attempts)</span>;
  } else if (m.status === "pending" && m.next_retry_at) {
    text = (
      <span className="text-gray-500">
        no reason yet, retry at {nyTime(m.next_retry_at)} (attempt {m.attempts + 1}/{m.max_attempts})
      </span>
    );
  } else {
    text = <span className="text-gray-500">explaining… (attempt {m.attempts + 1}/{m.max_attempts})</span>;
  }
  return (
    <p className="break-words mb-2">
      <span className="font-semibold mr-2">{label}</span>
      {text}
    </p>
  );
};

interface InterceptedItem {
  id: number;
  url: string;
  content: string;
  timestamp: number;
  source: string | null;
}

interface WatchlistBySector {
  sector: string;
  items: WatchlistItem[];
  avg_change_rel: number;
  premarket_change_rel: number;
}

interface WatchlistFilters {
  group_by: "all" | "by_sector" | "marked_only" | "price_intercept_only";
  sort_by: "regular_hours_change_rel" | "premarket_change_rel" | "abs_pre_minus_reg";
  order: "asc" | "desc";
}

function getDefaultSortBy(): "regular_hours_change_rel" | "premarket_change_rel" {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  const day = now.getDay(); // 0=Sun, 6=Sat
  const isWeekday = day >= 1 && day <= 5;
  if (!isWeekday) return "premarket_change_rel";
  const timeInMinutes = now.getHours() * 60 + now.getMinutes();
  const isMarketHours = timeInMinutes >= 570 && timeInMinutes < 960; // 9:30-16:00
  return isMarketHours ? "regular_hours_change_rel" : "premarket_change_rel";
}

interface WatchlistInputFormProps {
  newItemRequest: NewItemRequest;
  setNewItemRequest: React.Dispatch<React.SetStateAction<NewItemRequest>>;
  onSearch: () => void;
  onSend: () => void;
  formMode: "add" | "edit" | "duplicate";
  sectors: string[];
}

const NEW_SECTOR_VALUE = "__new__";

const WatchlistInputForm = ({
  newItemRequest,
  setNewItemRequest,
  onSearch,
  onSend,
  formMode,
  sectors,
}: WatchlistInputFormProps) => {
  const [isNewSector, setIsNewSector] = React.useState(false);

  const sendDisabled =
    newItemRequest.instrument_id === 0 || formMode === "duplicate";

  const getButtonLabel = () => {
    switch (formMode) {
      case "edit":
        return "Update";
      case "duplicate":
        return "Duplicate";
      default:
        return "Add";
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
      className="flex flex-col gap-2"
    >
      <div className="flex flex-row flex-wrap gap-2">
      <div className="form-group w-16">
        <input
          type="text"
          placeholder="invcom_id"
          className="bg-gray-300 cursor-not-allowed w-full"
          value={newItemRequest.instrument_id}
          readOnly
        />
      </div>

      <div className="form-group w-24">
        <input
          type="text"
          placeholder="Ticker"
          className="w-full"
          value={newItemRequest.ticker}
          onChange={(e) =>
            setNewItemRequest({ ...newItemRequest, ticker: e.target.value })
          }
        />
      </div>

      <div className="form-group flex-1 min-w-32">
        <input
          type="text"
          placeholder="Company Name"
          className="w-full"
          value={newItemRequest.company_name}
          onChange={(e) =>
            setNewItemRequest({
              ...newItemRequest,
              company_name: e.target.value,
            })
          }
        />
      </div>

      <div className="form-group flex gap-2">
        <select
          title="Sector"
          className={isNewSector ? "w-20" : ""}
          value={isNewSector ? NEW_SECTOR_VALUE : newItemRequest.sector}
          onChange={(e) => {
            if (e.target.value === NEW_SECTOR_VALUE) {
              setIsNewSector(true);
              setNewItemRequest({ ...newItemRequest, sector: "" });
            } else {
              setIsNewSector(false);
              setNewItemRequest({ ...newItemRequest, sector: e.target.value });
            }
          }}
        >
          <option value={NEW_SECTOR_VALUE}>New sector</option>
          {sectors.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {isNewSector && (
          <input
            type="text"
            placeholder="New sector name"
            value={newItemRequest.sector}
            onChange={(e) =>
              setNewItemRequest({ ...newItemRequest, sector: e.target.value })
            }
          />
        )}
      </div>

      <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      <button
        type="button"
        onClick={onSend}
        className={`btn ${sendDisabled ? "btn-disabled" : "btn-primary"} h-11`}
        disabled={sendDisabled}
      >
        {getButtonLabel()}
      </button>
      </div>
    </form>
  );
};

type SaveStatus = "idle" | "saving" | "saved" | "error" | "invalid";

const WatchlistItemDetails = ({
  item,
  deleteIntercept,
  setActiveArticleUrl,
  onNotificationStepChange,
}: {
  item: WatchlistItem;
  deleteIntercept: (investingId: number, interceptId: number) => void;
  setActiveArticleUrl: (url: string) => void;
  onNotificationStepChange: (investingId: number, step: number | null) => Promise<void>;
}) => {
  const initRadio = () => {
    const s = item.notification_percentage_step;
    if (s === null) return "none";
    if (s === 1) return "1%";
    if (s === 3) return "3%";
    if (s === 5) return "5%";
    return "custom";
  };

  const [selectedRadio, setSelectedRadio] = React.useState<string>(initRadio);
  const [customValue, setCustomValue] = React.useState<string>(() => {
    const s = item.notification_percentage_step;
    return s !== null && s !== 1 && s !== 3 && s !== 5 ? String(s) : "";
  });
  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>("idle");
  const [statusMessage, setStatusMessage] = React.useState<string>("");
  const [interceptSourceFilter, setInterceptSourceFilter] = React.useState<string>("All");
  const savedTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const customDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = React.useCallback(async (step: number | null) => {
    setSaveStatus("saving");
    setStatusMessage("Saving…");
    try {
      await onNotificationStepChange(item.investing_id, step);
      setSaveStatus("saved");
      setStatusMessage("Saved");
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => { setSaveStatus("idle"); setStatusMessage(""); }, 2000);
    } catch (err: any) {
      setSaveStatus("error");
      setStatusMessage(err?.response?.data?.message || err?.message || "Error saving");
    }
  }, [item.investing_id, onNotificationStepChange]);

  const handleRadioChange = (value: string) => {
    setSelectedRadio(value);
    if (saveStatus === "invalid") { setSaveStatus("idle"); setStatusMessage(""); }
    if (value === "none") save(null);
    else if (value === "1%") save(1);
    else if (value === "3%") save(3);
    else if (value === "5%") save(5);
    // "custom" — wait for the user to type a valid value
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setCustomValue(raw);
    if (customDebounceRef.current) clearTimeout(customDebounceRef.current);
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed > 0) {
      if (saveStatus === "invalid") { setSaveStatus("idle"); setStatusMessage(""); }
      customDebounceRef.current = setTimeout(() => save(parsed), 400);
    } else if (raw.length > 0) {
      setSaveStatus("invalid");
      setStatusMessage("invalid %");
    } else {
      if (saveStatus === "invalid") { setSaveStatus("idle"); setStatusMessage(""); }
    }
  };

  const statusColor: Record<SaveStatus, string> = {
    idle: "text-transparent",
    saving: "text-gray-400",
    saved: "text-green-500",
    error: "text-red-500",
    invalid: "text-amber-500",
  };

  const interceptSources = Array.from(
    new Set(item.intercepted.map((i) => i.source || "unknown")),
  ).sort();

  const filteredIntercepted =
    interceptSourceFilter === "All"
      ? item.intercepted
      : item.intercepted.filter((i) => (i.source || "unknown") === interceptSourceFilter);

  return (
  <div className="col-span-4 sm:col-span-7 px-1 pb-1 w-0 min-w-full">
      <p className="font-semibold">{item.company_name}</p>
    <div className="flex items-center gap-2 mb-1">
      <p className="font-semibold">
        Price {item.last_notified != null ? ` (last not. ${item.last_notified.toFixed(2)})` : "Intercept"}
      </p>
      <span className={`text-xs ${statusColor[saveStatus]}`}>{statusMessage || "."}</span>
      {!!item.last_notification_level && (
        <span
          className="text-xs text-gray-500"
          title="Last 10%-interval daily-change threshold crossed (auto-notified, not configurable)"
        >
          Daily: {item.last_notification_level > 0 ? "+" : ""}{item.last_notification_level}%
        </span>
      )}
    </div>
    <div className="flex flex-row flex-wrap items-center gap-4 mb-2">
      <label className="flex items-center gap-1">
        <input type="radio" name={`threshold-${item.investing_id}`} value="1%"
          checked={selectedRadio === "1%"} onChange={() => handleRadioChange("1%")} />
        <span>{item.last_price ? `1% (${(item.last_price * 0.01).toFixed(2)})` : "1%"}</span>
      </label>
      <label className="flex items-center gap-1">
        <input type="radio" name={`threshold-${item.investing_id}`} value="3%"
          checked={selectedRadio === "3%"} onChange={() => handleRadioChange("3%")} />
        <span>{item.last_price ? `3% (${(item.last_price * 0.03).toFixed(2)})` : "3%"}</span>
      </label>
      <label className="flex items-center gap-1">
        <input type="radio" name={`threshold-${item.investing_id}`} value="5%"
          checked={selectedRadio === "5%"} onChange={() => handleRadioChange("5%")} />
        <span>{item.last_price ? `5% (${(item.last_price * 0.05).toFixed(2)})` : "5%"}</span>
      </label>
      <label className="flex items-center gap-1 w-32">
        <input type="radio" name={`threshold-${item.investing_id}`} value="custom"
          checked={selectedRadio === "custom"} onChange={() => handleRadioChange("custom")} />
        <input
          type="text"
          placeholder="custom %"
          className="ml-1 w-20"
          value={selectedRadio === "custom" ? customValue : ""}
          onClick={() => setSelectedRadio("custom")}
          onChange={handleCustomChange}
        />
      </label>
      <label className="flex items-center gap-1">
        <input type="radio" name={`threshold-${item.investing_id}`} value="none"
          checked={selectedRadio === "none"} onChange={() => handleRadioChange("none")} />
        <span>none</span>
      </label>
    </div>

    {item.move_explanation && <MoveExplanationBlock m={item.move_explanation} />}

    <p className="font-semibold break-words">Intercepted:</p>
    {item.intercepted.length > 0 && (
      <div className="flex flex-row flex-wrap items-center gap-3 mb-1">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            name={`intercept-source-${item.investing_id}`}
            value="All"
            checked={interceptSourceFilter === "All"}
            onChange={() => setInterceptSourceFilter("All")}
          />
          <span>All</span>
        </label>
        {interceptSources.map((source) => (
          <label key={source} className="flex items-center gap-1">
            <input
              type="radio"
              name={`intercept-source-${item.investing_id}`}
              value={source}
              checked={interceptSourceFilter === source}
              onChange={() => setInterceptSourceFilter(source)}
            />
            <span>{source}</span>
          </label>
        ))}
      </div>
    )}
    {filteredIntercepted.length > 0 ? (
      filteredIntercepted.map((intercept) => (
        <div key={intercept.id} className="flex items-start gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              deleteIntercept(item.investing_id, intercept.id);
            }}
            title="Delete intercepted item"
            className="text-red-600 hover:text-red-800"
          >
            🗑️
          </button>
          <div className="break-words whitespace-pre-wrap">
            {intercept.content}
            <div className="flex gap-2 mt-1">
              <a href={intercept.url} target="_blank" rel="noreferrer">
                (source)
              </a>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveArticleUrl(intercept.url);
                }}
              >
                (text)
              </a>
            </div>
          </div>
        </div>
      ))
    ) : (
      <p className="text-gray-400">No intercepted items</p>
    )}
  </div>
  );
};

export const Watchlist = (props?: any) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [newItemRequest, setNewItemRequest] = React.useState<NewItemRequest>({
    instrument_id: 0,
    ticker: "",
    notes: "",
    sector: "",
    company_name: "",
    type: "",
    exchange_name: "",
  });

  const [sectors, setSectors] = React.useState<string[]>([]);

  const [formMode, setFormMode] = React.useState<"add" | "edit" | "duplicate">(
    "add",
  );

  const [instrumentList, setInstrumentList] = React.useState<
    Instrument[] | null
  >(null);

  const [watchlistBySector, setWatchlistBySector] = React.useState<
    WatchlistBySector[]
  >([]);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [filters, setFilters] = React.useState<WatchlistFilters>({
    group_by: "all",
    sort_by: getDefaultSortBy(),
    order: "desc",
  });

  const [tickerFilter, setTickerFilter] = React.useState<string>(
    () => props?.params?.ticker ?? "",
  );
  const [sectorFilter, setSectorFilter] = React.useState<string>("");

  const [error, setError] = React.useState<string>("");

  const [sseStatus, setSseStatus] = React.useState<SseStatus>("disconnected");
  const abortRef = React.useRef<AbortController | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const savedScrollTop = React.useRef<number>(0);

  const [expandedIntercepted, setExpandedIntercepted] = React.useState<
    Set<number>
  >(new Set());

  const [activeArticleUrl, setActiveArticleUrl] = React.useState<string>("");

  const [noteModal, setNoteModal] = React.useState<{ item: WatchlistItem; draft: string } | null>(null);

  const toggleIntercepted = (investingId: number) => {
    setExpandedIntercepted((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(investingId)) {
        newSet.delete(investingId);
      } else {
        newSet.add(investingId);
      }
      return newSet;
    });
  };

  // Optimistically delete an intercepted item by id for a given watchlist item (investingId).
  // Removes the intercepted item from local state immediately, calls backend to delete,
  // and reverts state if the request fails.
  const deleteIntercept = (investingId: number, interceptId: number) => {
    setError("");

    // snapshot previous state to allow revert on error
    const prevSnapshot = watchlistBySector;

    // apply optimistic update
    const newState = watchlistBySector.map((sector) => ({
      ...sector,
      items: sector.items.map((item) =>
        item.investing_id === investingId
          ? { ...item, intercepted: item.intercepted.filter((i) => i.id !== interceptId) }
          : item,
      ),
    }));

    setWatchlistBySector(newState);

    api
      .delete(`/watchlist-intercepted/${interceptId}`)
      .then(() => {
        // nothing to do on success; optimistic update already applied
      })
      .catch((err) => {
        console.error("Error deleting intercepted item:", err);
        setError(err.response?.data?.message || "Error deleting intercepted item.");
        // revert to previous snapshot
        setWatchlistBySector(prevSnapshot);
      });
  };

  const handleMark = (item: WatchlistItem) => {
    const prevSnapshot = watchlistBySector;
    setWatchlistBySector(watchlistBySector.map((sector) => ({
      ...sector,
      items: sector.items.map((i) =>
        i.investing_id === item.investing_id ? { ...i, marked: (() => {
          const now = new Date();
          const nyc = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
          const pad = (n: number) => String(n).padStart(2, "0");
          return `${nyc.getFullYear()}-${pad(nyc.getMonth() + 1)}-${pad(nyc.getDate())} ${pad(nyc.getHours())}:${pad(nyc.getMinutes())}:${pad(nyc.getSeconds())}`;
        })() } : i,
      ),
    })));
    api.post(`/watchlist/${item.investing_id}/mark`).catch((err) => {
      setError(err.response?.data?.message || "Error marking item.");
      setWatchlistBySector(prevSnapshot);
    });
  };

  const handleUnmark = (item: WatchlistItem) => {
    const prevSnapshot = watchlistBySector;
    setWatchlistBySector(watchlistBySector.map((sector) => ({
      ...sector,
      items: sector.items.map((i) =>
        i.investing_id === item.investing_id ? { ...i, marked: null } : i,
      ),
    })));
    api.post(`/watchlist/${item.investing_id}/unmark`).catch((err) => {
      setError(err.response?.data?.message || "Error unmarking item.");
      setWatchlistBySector(prevSnapshot);
    });
  };

  const handleNotificationStepChange = async (investingId: number, step: number | null): Promise<void> => {
    const prevSnapshot = watchlistBySector;
    setWatchlistBySector(watchlistBySector.map((sector) => ({
      ...sector,
      items: sector.items.map((i) =>
        i.investing_id === investingId ? { ...i, notification_percentage_step: step, last_notified: i.last_price } : i,
      ),
    })));
    try {
      await api.patch(`/watchlist/${investingId}/notification-step`, { step });
    } catch (err: any) {
      setError(err.response?.data?.message || "Error updating notification step.");
      setWatchlistBySector(prevSnapshot);
      throw err;
    }
  };

  const handleNoteUpdate = (investingId: number, notes: string) => {
    const prevSnapshot = watchlistBySector;
    setWatchlistBySector(watchlistBySector.map((sector) => ({
      ...sector,
      items: sector.items.map((i) =>
        i.investing_id === investingId ? { ...i, notes } : i,
      ),
    })));
    setNoteModal(null);
    api.patch(`/watchlist/${investingId}/notes`, { notes }).catch((err) => {
      setError(err.response?.data?.message || "Error updating notes.");
      setWatchlistBySector(prevSnapshot);
    });
  };

  // Check if instrument_id already exists in watchlist
  const checkForDuplicate = React.useCallback(
    (instrumentId: number) => {
      const exists = watchlistBySector.some((sector) =>
        sector.items.some((item) => item.investing_id === instrumentId),
      );
      return exists;
    },
    [watchlistBySector],
  );

  // Update form mode based on instrument_id
  React.useEffect(() => {
    if (newItemRequest.instrument_id === 0) {
      setFormMode("add");
    } else if (formMode !== "edit") {
      // Only check for duplicates when not in edit mode
      if (checkForDuplicate(newItemRequest.instrument_id)) {
        setFormMode("duplicate");
      } else {
        setFormMode("add");
      }
    }
  }, [newItemRequest.instrument_id, checkForDuplicate, formMode]);

  function fetchSectors() {
    api
      .get("/watchlist/sectors")
      .then((response) => {
        const data: string[] = response.data;
        setSectors(data);
        // Set default sector to first if current sector is empty
        if (!newItemRequest.sector && data.length > 0) {
          setNewItemRequest((prev) => ({
            ...prev,
            sector: data[0],
          }));
        }
      })
      .catch((err) => {
        console.error("Error fetching sectors:", err);
      });
  }

  React.useEffect(() => {
    fetchSectors();
  }, []);

  React.useEffect(() => {
    fetchWatchlist();
  }, [filters]);

  function search() {
    const tickerToSearch = newItemRequest.ticker.trim();
    if (!tickerToSearch) return;

    // Cancel any active edit when searching (but keep the ticker, notes, and sector)
    setNewItemRequest({
      ...newItemRequest,
      instrument_id: 0,
      ticker: tickerToSearch,
    });
    setFormMode("add");

    setError("");

    api
      .get(`/invcom-search?ticker=${tickerToSearch}`)
      .then((response) => {
        const data: Instrument[] = response.data;
        setInstrumentList(data);
      })
      .catch((error) => {
        console.error("Error searching for ticker:", error);

        setError(
          error.response?.data?.message || "Error searching for ticker.",
        );
      });
  }

  function handleInstrumentSelect(instrument: Instrument) {
    setNewItemRequest({
      ...newItemRequest,
      instrument_id: instrument.pair_ID,
      ticker: instrument.search_main_text,
      company_name: instrument.search_main_longtext,
      type: instrument.type ?? "",
      exchange_name: instrument.exchange_name ?? "",
    });
    setInstrumentList(null);
  }

  function handleSend() {
    setError("");
    setIsLoading(true);

    if (formMode === "edit") {
      // Update existing item
      api
        .put("/watchlist", {
          investing_id: newItemRequest.instrument_id,
          ticker: newItemRequest.ticker,
          notes: newItemRequest.notes,
          sector: newItemRequest.sector,
          company_name: newItemRequest.company_name,
        })
        .then(() => {
          resetForm();
          fetchSectors();
          fetchWatchlist();
        })
        .catch((error) => {
          console.error("Error updating watchlist item:", error);
          setError(
            error.response?.data?.message || "Error updating watchlist item.",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Add new item
      api
        .post("/watchlist", {
          investing_id: newItemRequest.instrument_id,
          ticker: newItemRequest.ticker,
          notes: newItemRequest.notes,
          sector: newItemRequest.sector,
          company_name: newItemRequest.company_name,
          type: newItemRequest.type,
          exchange_name: newItemRequest.exchange_name,
        })
        .then(() => {
          resetForm();
          fetchSectors();
          fetchWatchlist();
        })
        .catch((error) => {
          console.error("Error adding to watchlist:", error);
          setError(
            error.response?.data?.message || "Error adding to watchlist.",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  function handleEdit(item: WatchlistItem) {
    setNewItemRequest({
      instrument_id: item.investing_id,
      ticker: item.ticker,
      notes: item.notes,
      sector: item.sector,
      company_name: item.company_name,
      type: item.type,
      exchange_name: item.exchange_name,
    });
    setFormMode("edit");
  }

  function handleDelete(item: WatchlistItem) {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${item.ticker}" from the watchlist?`,
    );

    if (!confirmed) return;

    setError("");
    setIsLoading(true);

    api
      .delete(`/watchlist/${item.investing_id}`)
      .then(() => {
        fetchWatchlist();
      })
      .catch((error) => {
        console.error("Error deleting watchlist item:", error);
        setError(
          error.response?.data?.message || "Error deleting watchlist item.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function resetForm() {
    setNewItemRequest({
      instrument_id: 0,
      ticker: "",
      notes: "",
      sector: sectors.length > 0 ? sectors[0] : "",
      company_name: "",
      type: "",
      exchange_name: "",
    });
    setFormMode("add");
  }

  function fetchWatchlist() {
    // Abort any previous SSE connection
    if (abortRef.current) {
      abortRef.current.abort();
    }

    setError("");
    setWatchlistBySector([]);
    setIsLoading(true);
    setSseStatus("disconnected");

    const controller = new AbortController();
    abortRef.current = controller;

    const token = localStorage.getItem("token");
    const baseUrl = resolveEndpoint();
    const backendGroupBy = filters.group_by === "price_intercept_only" ? "all" : filters.group_by;
    const url = `${baseUrl}/watchlist?group_by=${backendGroupBy}&sort_by=${filters.sort_by}&order=${filters.order}`;

    let updateCount = 0;

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setIsLoading(false);

        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop()!;
          for (const part of parts) {
            if (part.startsWith("data: ")) {
              const data: WatchlistBySector[] = JSON.parse(part.slice(6));
              setWatchlistBySector(data);
              updateCount++;
              if (updateCount === 1) {
                setSseStatus("stale");
              } else if (updateCount === 2) {
                setSseStatus("current");
              }
            }
          }
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Error fetching watchlist:", err);
        setError("Error fetching watchlist.");
        setIsLoading(false);
      });
  }

  // Save scroll position and scroll to top when article opens; restore when it closes
  React.useEffect(() => {
    if (!containerRef.current) return;
    if (activeArticleUrl) {
      savedScrollTop.current = containerRef.current.scrollTop;
      containerRef.current.scrollTop = 0;
    } else {
      containerRef.current.scrollTop = savedScrollTop.current;
    }
  }, [activeArticleUrl]);

  // Cleanup SSE on unmount
  React.useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  function openTickerChart(ticker: string, instrumentId?: number, companyName?: string, type?: string, exchangeName?: string) {
    if (location.pathname.startsWith("/mobile")) {
      const params = new URLSearchParams({ ticker });
      if (instrumentId) params.set("instrument_id", String(instrumentId));
      if (companyName) params.set("company_name", companyName);
      if (type) params.set("type", type);
      if (exchangeName) params.set("exchange_name", exchangeName);
      navigate(`/mobile/real-time-chart?${params.toString()}`);
      return;
    }
    if (props?.containerApi) {
      addFloatingPanel(props.containerApi, "realtime_chart", {
        ticker: ticker,
        instrument_id: instrumentId,
        company_name: companyName,
        type: type,
        exchange_name: exchangeName,
      });
    }
  }

  function openIBKR(item: WatchlistItem) {
    const day = new Date().getDay(); // 0=Sun,1=Mon,2=Tue,3=Wed,4=Thu,5=Fri,6=Sat
    const req = {
      ticker: item.ticker,
      right: item.premarket_change_rel >= 0 ? "p" : "c",
      start: String(item.premarket_last),
      directional_limit: "0",
      skip_expiries: day === 4 || day === 5 ? "1" : "0",
      number_of_expiries: "1",
    };
    localStorage.setItem("ibkr_totalViewReq", JSON.stringify(req));
    if (props?.containerApi) {
      addFloatingPanel(props.containerApi, "ibkr", {});
    }
  }

  return (
    <div ref={containerRef} className="p-4 h-full overflow-auto relative">
      {location.pathname.startsWith("/mobile") && (
        <Link to="/mobile" className="btn btn-default mb-2">To Menu</Link>
      )}
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          fetchWatchlist();
        }}
        className="flex flex-col sm:flex-row gap-6 mb-4 items-start"
      >
        <div className="flex flex-col">
          <span className="font-semibold mb-1">Group By</span>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="group_by"
              value="all"
              checked={filters.group_by === "all"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  group_by: e.target.value as "all" | "by_sector" | "marked_only" | "price_intercept_only",
                })
              }
            />
            all
          </label>

          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="group_by"
              value="by_sector"
              checked={filters.group_by === "by_sector"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  group_by: e.target.value as "all" | "by_sector" | "marked_only" | "price_intercept_only",
                })
              }
            />
            by_sector
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="group_by"
              value="marked_only"
              checked={filters.group_by === "marked_only"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  group_by: e.target.value as "all" | "by_sector" | "marked_only" | "price_intercept_only",
                })
              }
            />
            marked_only
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="group_by"
              value="price_intercept_only"
              checked={filters.group_by === "price_intercept_only"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  group_by: e.target.value as "all" | "by_sector" | "marked_only" | "price_intercept_only",
                })
              }
            />
            price_intercept_only
          </label>
        </div>

        <div className="flex flex-col">
          <span className="font-semibold mb-1">Sort By</span>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="sort_by"
              value="regular_hours_change_rel"
              checked={filters.sort_by === "regular_hours_change_rel"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  sort_by: e.target.value as
                    | "regular_hours_change_rel"
                    | "premarket_change_rel"
                    | "abs_pre_minus_reg",
                })
              }
            />
            regular_hours_change_rel
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="sort_by"
              value="premarket_change_rel"
              checked={filters.sort_by === "premarket_change_rel"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  sort_by: e.target.value as
                    | "regular_hours_change_rel"
                    | "premarket_change_rel"
                    | "abs_pre_minus_reg",
                })
              }
            />
            premarket_change_rel
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="sort_by"
              value="abs_pre_minus_reg"
              checked={filters.sort_by === "abs_pre_minus_reg"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  sort_by: e.target.value as
                    | "regular_hours_change_rel"
                    | "premarket_change_rel"
                    | "abs_pre_minus_reg",
                })
              }
            />
            |pre - reg|
          </label>
        </div>

        <div className="flex flex-col">
          <span className="font-semibold mb-1">Order</span>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="order"
              value="desc"
              checked={filters.order === "desc"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  order: e.target.value as "asc" | "desc",
                })
              }
            />
            desc
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="order"
              value="asc"
              checked={filters.order === "asc"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  order: e.target.value as "asc" | "desc",
                })
              }
            />
            asc
          </label>
        </div>

        <div className="flex items-center justify-between gap-4">
        <button type="submit" className="btn btn-default h-11">
          Refresh
        </button>

        <div
        className="flex items-center gap-1.5 bg-black/60 rounded-full px-2 py-1 text-xs text-white"
        title={
          sseStatus === "disconnected"
            ? "No data received"
            : sseStatus === "stale"
              ? "Data received, waiting for update…"
              : "Data is current"
        }
      >
        <span
          className={`inline-block w-2.5 h-2.5 rounded-full ${
            sseStatus === "disconnected"
              ? "bg-red-500"
              : sseStatus === "stale"
                ? "bg-yellow-400"
                : "bg-green-500"
          }`}
        />
        {sseStatus === "disconnected"
          ? "No data"
          : sseStatus === "stale"
            ? "Stale"
            : "Live"}
        </div>
        </div>
      </form>

      <WatchlistInputForm
        newItemRequest={newItemRequest}
        setNewItemRequest={setNewItemRequest}
        onSearch={search}
        onSend={handleSend}
        formMode={formMode}
        sectors={sectors}
      />

      {instrumentList && (
        <div className="mt-4">
          <h2>Search results:</h2>
          {instrumentList.map((inst) => (
            <div key={inst.pair_ID} className="mb-2 space-x-2">
              <a
                onClick={() => handleInstrumentSelect(inst)}
                className="cursor-pointer"
              >
                {inst.pair_ID}: [{inst.search_main_text}]{" "}
                {inst.search_main_longtext} ({inst.search_main_subtext})
              </a>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex gap-4">
        <input
          type="text"
          placeholder="Search ticker or name..."
          value={tickerFilter}
          onChange={(e) => setTickerFilter(e.target.value)}
          className="border rounded px-2 py-1 w-60"
        />
        <input
          type="text"
          placeholder="Search sector..."
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          className="border rounded px-2 py-1 w-60"
        />
      </div>

      {isLoading && <div className="mt-4">Loading...</div>}

      {watchlistBySector
        .filter((sectorData) => {
          const tf = tickerFilter.trim().toLowerCase();
          const sf = sectorFilter.trim().toLowerCase();
          const matchesSector =
            sf.length < 2 ||
            sectorData.sector.toLowerCase().startsWith(sf);
          const matchesTicker =
            tf.length < 2 ||
            sectorData.items.some((item) =>
              item.ticker.toLowerCase().startsWith(tf) ||
              item.company_name.toLowerCase().startsWith(tf),
            );
          return matchesSector && matchesTicker;
        })
        .map((sectorData) => {
          return (
            <div
              key={sectorData.sector}
              className="border-r-2 border-l-2 block sm:inline-block sm:mr-4 mt-4 md:min-w-[600px]"
            >
              <div className="bg-slate-300 p-1 flex justify-between">
                <span>{sectorData.sector}</span>
                <span>
                  {sectorData.avg_change_rel.toFixed(2)}% (pre:{" "}
                  {sectorData.premarket_change_rel.toFixed(2)}%)
                </span>
              </div>
              <div className="grid grid-cols-[auto_auto_auto_auto] sm:grid-cols-[auto_auto_auto_auto_auto_auto_auto] gap-x-4">
                {sectorData.items
                  .filter((item) => {
                    if (filters.group_by === "price_intercept_only") {
                      return item.notification_percentage_step !== null;
                    }
                    if (filters.group_by !== "all") return true;
                    const tf = tickerFilter.trim().toLowerCase();
                    if (tf.length < 2) return true;
                    return (
                      item.ticker.toLowerCase().startsWith(tf) ||
                      item.company_name.toLowerCase().startsWith(tf)
                    );
                  })
                  .map((item) => {
                  const tf = tickerFilter.trim().toLowerCase();
                  const isItemMatch =
                    tf.length >= 2 &&
                    (item.ticker.toLowerCase().startsWith(tf) ||
                      item.company_name.toLowerCase().startsWith(tf));
                  const highlightClass = isItemMatch ? "bg-yellow-200" : "";
                  return (
                    <div
                      key={item.investing_id}
                      className={`col-span-4 sm:col-span-7 grid grid-cols-subgrid ${highlightClass}`}
                    >
                      <a
                        className={`px-1 pt-2 ${item.premarket_active ? "text-gray-400" : ""}`}
                        title={item.company_name}
                        onClick={() =>
                          openTickerChart(item.ticker, item.investing_id, item.company_name, item.type, item.exchange_name)
                        }
                      >
                        {item.ticker}
                      </a>
                      <span
                        className={`px-1 pt-2 text-right ${item.premarket_active ? "text-gray-400" : ""}`}
                      >
                        {item.last_price.toFixed(2)}
                      </span>
                      <span
                        className={`px-1 pt-2 text-right ${item.premarket_active ? "text-gray-400" : ""}`}
                      >
                        {item.change_abs.toFixed(2)}
                      </span>
                      <span
                        className={`px-1 pt-2 text-right ${item.premarket_active ? "text-gray-400" : ""}`}
                      >
                        {item.change_rel.toFixed(2)}%
                      </span>
                      <a
                        className="hidden sm:block cursor-pointer px-1 pt-2"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </a>
                      <a
                        className="hidden sm:block cursor-pointer px-1 pt-2"
                        onClick={() => handleDelete(item)}
                      >
                        Delete
                      </a>
                      <button
                        type="button"
                        className={`hidden sm:block px-1 pt-2 text-sm rounded transition-colors ${item.marked ? "bg-gray-700 text-white hover:bg-gray-600" : "hover:bg-gray-200"}`}
                        onClick={() => item.marked ? handleUnmark(item) : handleMark(item)}
                        title={item.marked ?? undefined}
                      >
                        {item.marked ? "Unmark" : "Mark"}
                      </button>

                      <span
                        className={`px-1 pb-1 ${!item.premarket_active ? "text-gray-400" : ""}`}
                      >
                        pre:
                      </span>
                      <span
                        className={`px-1 pb-1 text-right ${!item.premarket_active ? "text-gray-400" : ""}`}
                      >
                        {item.premarket_last.toFixed(2)}
                      </span>
                      <span
                        className={`px-1 pb-1 text-right ${!item.premarket_active ? "text-gray-400" : ""}`}
                      >
                        {item.premarket_change_abs.toFixed(2)}
                      </span>
                      <span
                        className={`px-1 pb-1 text-right ${!item.premarket_active ? "text-gray-400" : ""}`}
                      >
                        {item.premarket_change_rel.toFixed(2)}%
                      </span>
                      <a
                        onClick={() => toggleIntercepted(item.investing_id)}
                        className={`hidden sm:block cursor-pointer sm:col-span-2 px-1 pb-1 ${item.intercepted.length === 0 ? "text-gray-400" : "font-bold"}`}
                      >
                        {item.intercepted.length} intercepted{" "}
                        {expandedIntercepted.has(item.investing_id) ? "▲" : "▼"}
                      </a>
                      <button
                        type="button"
                        className="hidden sm:block px-1 pb-1 text-sm rounded transition-colors hover:bg-gray-200"
                        onClick={() => openIBKR(item)}
                      >
                        IBKR
                      </button>

                      {/* Mobile-only buttons row */}
                      <div className="sm:hidden col-span-4 flex gap-4 px-1 pb-1 items-start">
                        <button
                          type="button"
                          onClick={() => toggleIntercepted(item.investing_id)}
                          className={`text-sm ${item.intercepted.length === 0 ? "text-gray-400" : "font-bold"}`}
                        >
                          {item.intercepted.length} intercepted{" "}
                          {expandedIntercepted.has(item.investing_id) ? "▲" : "▼"}
                        </button>
                        <div className="flex flex-col gap-0.5 text-sm">
                          <button type="button" onClick={() => handleEdit(item)}>Edit</button>
                          <button type="button" onClick={() => handleDelete(item)}>Delete</button>
                        </div>
                        <div className="flex flex-col gap-0.5 text-sm">
                          <button
                            type="button"
                            className={`rounded transition-colors ${item.marked ? "bg-gray-700 text-white hover:bg-gray-600" : "hover:bg-gray-200"}`}
                            onClick={() => item.marked ? handleUnmark(item) : handleMark(item)}
                            title={item.marked ?? undefined}
                          >
                            {item.marked ? "Unmark" : "Mark"}
                          </button>
                          <button
                            type="button"
                            className="rounded transition-colors hover:bg-gray-200"
                            onClick={() => openIBKR(item)}
                          >
                            IBKR
                          </button>
                        </div>
                      </div>

                      {expandedIntercepted.has(item.investing_id) && (
                        <WatchlistItemDetails
                          item={item}
                          deleteIntercept={deleteIntercept}
                          setActiveArticleUrl={setActiveArticleUrl}
                          onNotificationStepChange={handleNotificationStepChange}
                        />
                      )}

                      <div className="col-span-4 sm:col-span-7 border-b border-gray-300"></div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

      {activeArticleUrl !== "" && (
        <ArticleViewer
          url={activeArticleUrl}
          setActiveArticleUrl={setActiveArticleUrl}
        />
      )}

      {noteModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setNoteModal(null); }}
        >
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-4 flex flex-col gap-3">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-base">
                Note — {noteModal.item.ticker}
              </span>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                onClick={() => setNoteModal(null)}
              >
                ✕
              </button>
            </div>
            <textarea
              className="w-full border rounded px-2 py-1 resize-y text-sm"
              rows={4}
              value={noteModal.draft}
              onChange={(e) => setNoteModal({ ...noteModal, draft: e.target.value })}
              placeholder="Enter a note…"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="btn btn-default"
                onClick={() => setNoteModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleNoteUpdate(noteModal.item.investing_id, noteModal.draft)}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
